import ExpandMore from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { StatusChip, StatusChipColor } from "components/StatusChip";
import {
  BlockedFacility,
  BlockedFacilityReason,
  InventoryChangeDetail,
  RunSummary,
} from "encamp-shared/src/hmbp/wadmRunSummary";

const BLOCKED_REASON_LABELS: Record<BlockedFacilityReason, string> = {
  unmatchedProfileCode: "Unmapped profile code",
  ambiguousChemical: "Ambiguous chemical match",
  missingChemical: "Chemical missing from catalog",
  colocationParentNotInCalifornia: "Co-located reporting facility not in California",
};

/**
 * Inventory-change "Kind" chip. created → success, updated → info; an unchanged
 * row uses the plain grey outlined default from the chip spec (no semantic
 * color), since it carries no signal.
 */
function InventoryKindChip({ kind }: { kind: InventoryChangeDetail["kind"] }) {
  if (kind === "created") return <StatusChip label={kind} color="success" />;
  if (kind === "updated") return <StatusChip label={kind} color="info" />;
  return <StatusChip label={kind} color="default" variant="outlined" />;
}

/**
 * Renders the WADM run summary stored on `Job.output` for `PROCESS_AMAZON_WADM`
 * jobs. Two accordions:
 *   - "Run Summary" — top-line counts, CUPA breakdown, chip lists for
 *     unmatched profile codes / missing chemicals / unmatched site codes /
 *     facilities with a pre-existing open UPDATE that this run skipped.
 *   - "Inventory Changes" — per-row before/after table.
 *
 * The caller is responsible for gating render on
 * `task === ProcessAmazonWadm && isWadmRunSummary(output)` so this component
 * receives a fully-typed summary.
 */
export function WadmRunSummarySection({ summary }: { summary: RunSummary }) {
  return (
    <Stack spacing={2}>
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle1" fontWeight="medium">
            WADM Run Summary
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <RunSummaryBody summary={summary} />
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle1" fontWeight="medium">
            Inventory Changes ({summary.inventoryChanges.length})
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <InventoryChangesTable changes={summary.inventoryChanges} />
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
}

function RunSummaryBody({ summary }: { summary: RunSummary }) {
  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={4} flexWrap="wrap">
        <CounterStat
          label="Facilities matched"
          value={summary.facilitiesMatched}
          secondary={`of ${summary.facilitiesAttempted.toLocaleString()} attempted`}
        />
        <CounterStat label="Items updated" value={summary.itemsUpdated} />
        <CounterStat label="Reports created" value={summary.reportsCreated} />
      </Stack>

      <BlockedFacilitiesTable blockedFacilities={summary.blockedFacilities ?? []} />

      {summary.cupaBreakdown.length > 0 && (
        <Box>
          <Typography variant="overline" color="text.secondary">
            CUPA breakdown
          </Typography>
          <TableContainer sx={{ mt: 0.5 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>CUPA</TableCell>
                  <TableCell>County</TableCell>
                  <TableCell align="right">Reports</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summary.cupaBreakdown.map((c, i) => (
                  <TableRow key={`${c.lepcName ?? "null"}::${c.county ?? "null"}::${i}`}>
                    <TableCell>{c.lepcName ?? "—"}</TableCell>
                    <TableCell>{c.county ?? "—"}</TableCell>
                    <TableCell align="right">{c.reportCount}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <ChipList
        label="Unmatched profile codes"
        color="error"
        values={summary.unmatchedProfileCodes}
      />
      <ChipList label="Missing chemicals" color="error" values={summary.missingChemicals} />
      <ChipList label="Unmatched site codes" color="error" values={summary.unmatchedSiteCodes} />
      <ChipList
        label="Facilities with an open UPDATE (skipped this run)"
        color="info"
        values={summary.facilityIdsWithExistingOpenUpdate}
      />
    </Stack>
  );
}

function CounterStat({
  label,
  value,
  secondary,
}: {
  label: string;
  value: number;
  secondary?: string;
}) {
  return (
    <Box>
      <Typography variant="overline" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5" fontWeight="medium">
        {value.toLocaleString()}
      </Typography>
      {secondary !== undefined && (
        <Typography variant="caption" color="text.secondary">
          {secondary}
        </Typography>
      )}
    </Box>
  );
}

function ChipList({
  label,
  color,
  values,
}: {
  label: string;
  color: StatusChipColor;
  values: string[];
}) {
  if (values.length === 0) return null;
  return (
    <Box>
      <Typography variant="overline" color="text.secondary">
        {label} ({values.length})
      </Typography>
      <Box display="flex" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
        {values.map((v) => (
          <StatusChip key={v} label={v} color={color} />
        ))}
      </Box>
    </Box>
  );
}

function BlockedFacilitiesTable({ blockedFacilities }: { blockedFacilities: BlockedFacility[] }) {
  if (blockedFacilities.length === 0) return null;
  return (
    <Box>
      <Typography variant="overline" color="error">
        Skipped facilities ({blockedFacilities.length})
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
        These facilities were not written this run because some of their rows could not be mapped.
        Fix the mapping(s) below and re-run the cycle.
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Site Code</TableCell>
              <TableCell>Facility</TableCell>
              <TableCell>Reasons</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {blockedFacilities.map((b) => (
              <TableRow key={b.siteCode}>
                <TableCell>{b.siteCode}</TableCell>
                <TableCell>{b.facilityId ?? "—"}</TableCell>
                <TableCell>
                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                    {b.reasons.map((r) => (
                      <StatusChip key={r} color="error" label={BLOCKED_REASON_LABELS[r]} />
                    ))}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function InventoryChangesTable({ changes }: { changes: InventoryChangeDetail[] }) {
  if (changes.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No inventory rows were created or updated this run.
      </Typography>
    );
  }
  return (
    // Cap the height so very large runs (thousands of rows) scroll within a
    // bounded region instead of expanding the page inline. Header stays
    // sticky as the user scrolls.
    <TableContainer sx={{ maxHeight: 480 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Facility</TableCell>
            <TableCell>Chemical</TableCell>
            <TableCell>Kind</TableCell>
            <TableCell align="right">Prior Max</TableCell>
            <TableCell align="right">New Max</TableCell>
            <TableCell align="right">Max Code</TableCell>
            <TableCell align="right">Prior Avg</TableCell>
            <TableCell align="right">New Avg</TableCell>
            <TableCell align="right">Avg Code</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {changes.map((c) => (
            <TableRow key={`${c.facilityId}::${c.chemicalId}`}>
              <TableCell>{c.facilityName ?? c.facilityId}</TableCell>
              <TableCell>{c.chemicalName ?? c.chemicalId}</TableCell>
              <TableCell>
                <InventoryKindChip kind={c.kind} />
              </TableCell>
              <TableCell align="right">{formatAmount(c.priorMaxAmount)}</TableCell>
              <TableCell align="right">{formatAmount(c.newMaxAmount)}</TableCell>
              <TableCell align="right">
                {formatCodeTransition(c.priorMaxAmountCode, c.newMaxAmountCode)}
              </TableCell>
              <TableCell align="right">{formatAmount(c.priorAverageAmount)}</TableCell>
              <TableCell align="right">{formatAmount(c.newAverageAmount)}</TableCell>
              <TableCell align="right">
                {formatCodeTransition(c.priorAverageAmountCode, c.newAverageAmountCode)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function formatAmount(n: number | null): string {
  if (n === null) return "—";
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function formatCodeTransition(prior: number | null, next: number | null): string {
  if (prior === null && next === null) return "—";
  if (prior === null) return String(next);
  if (prior === next) return String(next);
  return `${prior} → ${next ?? "—"}`;
}
